import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Card } from '@mui/material';

const SCROLL_CONTAINER_SELECTOR = '[data-scroll-container]';

interface DashboardControlsStickyShellProps {
  children: React.ReactNode;
}

interface PinGeometry {
  top: number;
  left: number;
  width: number;
}

export default function DashboardControlsStickyShell({
  children
}: DashboardControlsStickyShellProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [cardHeight, setCardHeight] = useState(0);
  const [pinGeometry, setPinGeometry] = useState<PinGeometry>({ top: 0, left: 0, width: 0 });

  const getScrollRoot = useCallback((): Element | null => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return document.querySelector(SCROLL_CONTAINER_SELECTOR);
    }
    return sentinel.closest(SCROLL_CONTAINER_SELECTOR) ?? document.querySelector(SCROLL_CONTAINER_SELECTOR);
  }, []);

  const updatePinGeometry = useCallback(() => {
    const scrollRoot = getScrollRoot();
    const card = cardRef.current;
    if (!scrollRoot || !card) {
      return;
    }

    const scrollRect = scrollRoot.getBoundingClientRect();
    setPinGeometry({
      top: scrollRect.top,
      left: scrollRect.left,
      width: scrollRect.width
    });
    setCardHeight(card.offsetHeight);
  }, [getScrollRoot]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollRoot = getScrollRoot();
    if (!sentinel || !scrollRoot) {
      return undefined;
    }

    updatePinGeometry();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }
        const isPinned = !entry.isIntersecting;
        setPinned(isPinned);
        setStuck(isPinned);
        if (isPinned) {
          updatePinGeometry();
        }
      },
      {
        root: scrollRoot,
        threshold: 0
      }
    );

    observer.observe(sentinel);

    const scrollRootEl = scrollRoot as HTMLElement;
    scrollRootEl.addEventListener('scroll', updatePinGeometry, { passive: true });
    window.addEventListener('resize', updatePinGeometry);
    window.addEventListener('scroll', updatePinGeometry, { passive: true });

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePinGeometry);
      resizeObserver.observe(scrollRoot);
      if (cardRef.current) {
        resizeObserver.observe(cardRef.current);
      }
    }

    return () => {
      observer.disconnect();
      scrollRootEl.removeEventListener('scroll', updatePinGeometry);
      window.removeEventListener('resize', updatePinGeometry);
      window.removeEventListener('scroll', updatePinGeometry);
      resizeObserver?.disconnect();
    };
  }, [getScrollRoot, updatePinGeometry]);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        ref={sentinelRef}
        aria-hidden
        sx={{
          height: 1,
          width: '100%',
          pointerEvents: 'none',
          visibility: 'hidden'
        }}
      />
      {pinned && cardHeight > 0 && (
        <Box aria-hidden sx={{ height: cardHeight, mb: 0 }} />
      )}
      <Card
        ref={cardRef}
        sx={{
          ...(pinned
            ? {
                position: 'fixed',
                top: pinGeometry.top,
                left: pinGeometry.left,
                width: pinGeometry.width,
                zIndex: 1099
              }
            : {
                position: 'relative'
              }),
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: stuck ? 2 : 0,
          transition: 'box-shadow 0.15s ease-in-out'
        }}
      >
        {children}
      </Card>
    </Box>
  );
}
